import { MedplumClient, createReference } from '@medplum/core';
import { Bundle, BundleEntry, Observation, Patient } from '@medplum/fhirtypes';
import { SYSTEMS, HL7, BIRTH_MEASUREMENT_CODES } from '@/consts/systems.consts';
import {
  BirthMeasurementsFormState,
  BirthMeasurementsData,
} from './medical-history.types';

// -----------------------------------------------------------------------------
// Initial Form State
// -----------------------------------------------------------------------------

export const createInitialBirthMeasurementsFormState =
  (): BirthMeasurementsFormState => ({
    birthWeight: null,
    birthLength: null,
    birthHeadCircumference: null,
  });

// -----------------------------------------------------------------------------
// Map Observations to Form State
// -----------------------------------------------------------------------------

export const mapObservationsToFormState = (
  data: BirthMeasurementsData
): BirthMeasurementsFormState => {
  return {
    birthWeight: data.birthWeight?.valueQuantity?.value ?? null,
    birthLength: data.birthLength?.valueQuantity?.value ?? null,
    birthHeadCircumference:
      data.birthHeadCircumference?.valueQuantity?.value ?? null,
  };
};

// -----------------------------------------------------------------------------
// Create Birth Observation
// -----------------------------------------------------------------------------

export const createBirthObservation = (
  patient: Patient,
  loincCode: string,
  snomedCode: string,
  loincDisplay: string,
  snomedDisplay: string,
  value: number | null,
  unit: string
): Observation | undefined => {
  if (value === null || value === undefined || isNaN(value)) {
    return undefined;
  }

  return {
    resourceType: 'Observation',
    status: 'final',
    category: [
      {
        coding: [
          {
            system: HL7.TERMINOLOGY.CS.OBSERVATION.CATEGORY.BASE,
            code: 'vital-signs',
            display: 'Vital Signs',
          },
        ],
      },
    ],
    code: {
      coding: [
        {
          system: SYSTEMS.LOINC,
          code: loincCode,
          display: loincDisplay,
        },
        {
          system: SYSTEMS.SNOMED,
          code: snomedCode,
          display: snomedDisplay,
        },
      ],
      text: snomedDisplay,
    },
    subject: createReference(patient),
    effectiveDateTime: patient.birthDate || new Date().toISOString(),
    valueQuantity: {
      value,
      unit,
      system: SYSTEMS.UCUM,
      code: unit,
    },
  };
};

// -----------------------------------------------------------------------------
// Build Bundle Entries for Save
// -----------------------------------------------------------------------------

export const buildBirthMeasurementsBundleEntries = (
  patient: Patient,
  formState: BirthMeasurementsFormState,
  existingData?: BirthMeasurementsData
): BundleEntry[] => {
  const entries: BundleEntry[] = [];

  // Birth Weight
  if (formState.birthWeight !== null) {
    const obs = createBirthObservation(
      patient,
      BIRTH_MEASUREMENT_CODES.WEIGHT.loinc,
      BIRTH_MEASUREMENT_CODES.WEIGHT.snomed,
      BIRTH_MEASUREMENT_CODES.WEIGHT.loincDisplay,
      BIRTH_MEASUREMENT_CODES.WEIGHT.snomedDisplay,
      formState.birthWeight,
      BIRTH_MEASUREMENT_CODES.WEIGHT.unit
    );
    if (obs) {
      if (existingData?.birthWeight?.id) {
        entries.push({
          resource: { ...obs, id: existingData.birthWeight.id },
          request: {
            method: 'PUT',
            url: `Observation/${existingData.birthWeight.id}`,
          },
        });
      } else {
        entries.push({
          resource: obs,
          request: { method: 'POST', url: 'Observation' },
        });
      }
    }
  } else if (existingData?.birthWeight?.id) {
    // Delete existing if value is cleared
    entries.push({
      request: {
        method: 'DELETE',
        url: `Observation/${existingData.birthWeight.id}`,
      },
    });
  }

  // Birth Length
  if (formState.birthLength !== null) {
    const obs = createBirthObservation(
      patient,
      BIRTH_MEASUREMENT_CODES.LENGTH.loinc,
      BIRTH_MEASUREMENT_CODES.LENGTH.snomed,
      BIRTH_MEASUREMENT_CODES.LENGTH.loincDisplay,
      BIRTH_MEASUREMENT_CODES.LENGTH.snomedDisplay,
      formState.birthLength,
      BIRTH_MEASUREMENT_CODES.LENGTH.unit
    );
    if (obs) {
      if (existingData?.birthLength?.id) {
        entries.push({
          resource: { ...obs, id: existingData.birthLength.id },
          request: {
            method: 'PUT',
            url: `Observation/${existingData.birthLength.id}`,
          },
        });
      } else {
        entries.push({
          resource: obs,
          request: { method: 'POST', url: 'Observation' },
        });
      }
    }
  } else if (existingData?.birthLength?.id) {
    entries.push({
      request: {
        method: 'DELETE',
        url: `Observation/${existingData.birthLength.id}`,
      },
    });
  }

  // Birth Head Circumference
  if (formState.birthHeadCircumference !== null) {
    const obs = createBirthObservation(
      patient,
      BIRTH_MEASUREMENT_CODES.HEAD_CIRCUMFERENCE.loinc,
      BIRTH_MEASUREMENT_CODES.HEAD_CIRCUMFERENCE.snomed,
      BIRTH_MEASUREMENT_CODES.HEAD_CIRCUMFERENCE.loincDisplay,
      BIRTH_MEASUREMENT_CODES.HEAD_CIRCUMFERENCE.snomedDisplay,
      formState.birthHeadCircumference,
      BIRTH_MEASUREMENT_CODES.HEAD_CIRCUMFERENCE.unit
    );
    if (obs) {
      if (existingData?.birthHeadCircumference?.id) {
        entries.push({
          resource: { ...obs, id: existingData.birthHeadCircumference.id },
          request: {
            method: 'PUT',
            url: `Observation/${existingData.birthHeadCircumference.id}`,
          },
        });
      } else {
        entries.push({
          resource: obs,
          request: { method: 'POST', url: 'Observation' },
        });
      }
    }
  } else if (existingData?.birthHeadCircumference?.id) {
    entries.push({
      request: {
        method: 'DELETE',
        url: `Observation/${existingData.birthHeadCircumference.id}`,
      },
    });
  }

  return entries;
};

// -----------------------------------------------------------------------------
// Save Birth Measurements
// -----------------------------------------------------------------------------

export const saveBirthMeasurements = async (
  medplum: MedplumClient,
  patient: Patient,
  formState: BirthMeasurementsFormState,
  existingData?: BirthMeasurementsData
): Promise<void> => {
  const entries = buildBirthMeasurementsBundleEntries(
    patient,
    formState,
    existingData
  );

  if (entries.length === 0) {
    return;
  }

  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };

  await medplum.executeBatch(bundle);
};

// -----------------------------------------------------------------------------
// Fetch Birth Observations
// -----------------------------------------------------------------------------

export const fetchBirthObservations = async (
  medplum: MedplumClient,
  patient: Patient
): Promise<BirthMeasurementsData> => {
  if (!patient?.id) {
    return {
      birthWeight: undefined,
      birthLength: undefined,
      birthHeadCircumference: undefined,
    };
  }

  // Search for all birth-related observations
  const codes = [
    BIRTH_MEASUREMENT_CODES.WEIGHT.snomed,
    BIRTH_MEASUREMENT_CODES.LENGTH.snomed,
    BIRTH_MEASUREMENT_CODES.HEAD_CIRCUMFERENCE.snomed,
  ].join(',');

  const observations = await medplum.searchResources('Observation', {
    subject: `Patient/${patient.id}`,
    code: codes,
    _sort: '-date',
    _count: '100',
  });

  const findObsBySnomedCode = (code: string): Observation | undefined => {
    return observations.find((obs) =>
      obs.code?.coding?.some(
        (c) => c.system === SYSTEMS.SNOMED && c.code === code
      )
    );
  };

  return {
    birthWeight: findObsBySnomedCode(BIRTH_MEASUREMENT_CODES.WEIGHT.snomed),
    birthLength: findObsBySnomedCode(BIRTH_MEASUREMENT_CODES.LENGTH.snomed),
    birthHeadCircumference: findObsBySnomedCode(
      BIRTH_MEASUREMENT_CODES.HEAD_CIRCUMFERENCE.snomed
    ),
  };
};

// -----------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------

export const validateBirthMeasurementsForm = (
  formState: BirthMeasurementsFormState
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  if (formState.birthWeight !== null) {
    if (formState.birthWeight < 0 || formState.birthWeight > 10000) {
      errors.birthWeight = 'Το βάρος πρέπει να είναι μεταξύ 0 και 10000 gr';
    }
  }

  if (formState.birthLength !== null) {
    if (formState.birthLength < 0 || formState.birthLength > 100) {
      errors.birthLength = 'Το μήκος πρέπει να είναι μεταξύ 0 και 100 cm';
    }
  }

  if (formState.birthHeadCircumference !== null) {
    if (
      formState.birthHeadCircumference < 0 ||
      formState.birthHeadCircumference > 100
    ) {
      errors.birthHeadCircumference =
        'Η περίμετρος κεφαλής πρέπει να είναι μεταξύ 0 και 100 cm';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// -----------------------------------------------------------------------------
// Check if form has data
// -----------------------------------------------------------------------------

export const hasAnyBirthMeasurement = (
  formState: BirthMeasurementsFormState
): boolean => {
  return (
    formState.birthWeight !== null ||
    formState.birthLength !== null ||
    formState.birthHeadCircumference !== null
  );
};
